import React, { useState, useEffect, useCallback } from "react";
import ApiService from "../Components/apiService";
import {
  Table,
  Tooltip,
  Popconfirm,
  Checkbox,
  message,
  Button,
  Card,
  Row,
  Col,
  Typography,
} from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import CreateClass from "./CreateClass"; // Import the CreateClass component
import { BASE_URL, DateFormat, DateTimeFormat } from "../Components/Constant";
import { convertDateToUserTimezone } from "../Components/Utility";
import { useSelector } from "react-redux";

const { Title } = Typography;

const DEFAULT_PAGE_SIZE = 10;

const ClassesList = () => {
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [classData, setClassData] = useState("");
  const [isCreatingClass, setIsCreatingClass] = useState(false); // Track if we're creating a new class
  const [loading, setLoading] = useState(false);

  // Server-side pagination state
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0,
  });

  const security = useSelector((state) => state.data.items[0]);

  // Fetch a single page of classes from the backend using limit/offset
  const fetchClasses = useCallback(
    async (page = 1, pageSize = DEFAULT_PAGE_SIZE) => {
      const limit = pageSize;
      const offset = (page - 1) * pageSize;

      setLoading(true);
      try {
        const apiService = new ApiService(
          `${BASE_URL}/ruby_class?limit=${limit}&offset=${offset}`,
          { "Content-Type": "application/json" },
          "GET",
        );
        const res = await apiService.makeCall();

        setClasses(Array.isArray(res.classes) ? res.classes : []);
        setPagination((prev) => ({
          ...prev,
          current: page,
          pageSize,
          total: typeof res.total === "number" ? res.total : 0,
        }));
      } catch (error) {
        console.error("Error fetching classes:", error);
        message.error("Failed to load classes. Please try again.");
        setClasses([]);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    // Fetch first page on mount
    fetchClasses(1, DEFAULT_PAGE_SIZE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Called whenever the user changes page or page size in the Table
  const handleTableChange = (paginationConfig) => {
    const { current, pageSize } = paginationConfig;
    fetchClasses(current, pageSize);
  };

  const handleDeleteClass = async (className) => {
    if (security?.system_settings?.ruby_class?.delete === false) {
      message.error("You do not have permission to delete ");
      return;
    }
    try {
      const apiService = new ApiService(
        `${BASE_URL}/ruby_class/${className}`,
        { "Content-Type": "application/json" },
        "DELETE",
      );
      await apiService.makeCall();
      message.success("Class deleted successfully!");

      // If we deleted the last row on a page beyond page 1, step back a page
      const isLastRowOnPage = classes.length === 1 && pagination.current > 1;
      const nextPage = isLastRowOnPage
        ? pagination.current - 1
        : pagination.current;
      fetchClasses(nextPage, pagination.pageSize); // Refresh the current page after deletion
    } catch (error) {
      console.error("Error deleting class:", error);
      message.error("Failed to delete class.");
    }
  };

  const fetchClassData = async (record) => {
    if (security?.system_settings?.ruby_class?.edit === false) {
      message.error("You do not have permission to edit ");
      return;
    }
    try {
      const apiService = new ApiService(
        `${BASE_URL}/ruby_class/${record.class_name}`,
        { "Content-Type": "application/json" },
        "GET",
      );
      const res = await apiService.makeCall();
      const updated = { ...record, classcode: res.Class };
      setClassData(updated); // Set the class data to prefill the form
      setSelectedClass(updated);
      setIsCreatingClass(true); // Trigger the class creation view
    } catch (error) {
      console.error("Error fetching class data:", error);
      message.error("Failed to load class details.");
    }
  };

  const columns = [
    {
      title: "Class Name",
      dataIndex: "class_name",
      key: "class_name",
      // NOTE: this sorts only the rows on the current page (server-paginated data).
      // For true full-dataset sorting, push sort_field/sort_dir to the backend.
      sorter: (a, b) => a.class_name.localeCompare(b.class_name),
      render: (text, record) => (
        <Link
          onClick={() => fetchClassData(record)}
          style={{ cursor: "pointer" }}
        >
          {text}
        </Link>
      ),
    },
    {
      title: "Active",
      dataIndex: "active",
      key: "active",
      render: (text, record) => <Checkbox checked={record.active} disabled />,
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      render: (text) =>
        text && dayjs(text).isValid()
          ? convertDateToUserTimezone(text, DateTimeFormat)
          : "",
    },
    {
      title: "Updated At",
      dataIndex: "updated_at",
      key: "updated_at",
      sorter: (a, b) => new Date(a.updated_at) - new Date(b.updated_at),
      render: (text) =>
        text && dayjs(text).isValid()
          ? convertDateToUserTimezone(text, DateTimeFormat)
          : "",
    },
    {
      title: "Action",
      key: "action",
      render: (text, record) => (
        <>
          <Tooltip title="Edit">
            <EditOutlined
              onClick={() => fetchClassData(record)}
              style={{ marginRight: 5, fontSize: "14px", cursor: "pointer" }}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm
              title="Are you sure you want to delete this class?"
              onConfirm={() => handleDeleteClass(record.class_name)}
              okText="Yes"
              cancelText="No"
            >
              <DeleteOutlined
                style={{ color: "red", fontSize: "14px", cursor: "pointer" }}
              />
            </Popconfirm>
          </Tooltip>
        </>
      ),
    },
  ];

  const handleCreateClass = () => {
    if (security?.system_settings?.ruby_class?.create === false) {
      message.error("You do not have permission to create ");
      return;
    }
    setIsCreatingClass(true);
    setClassData("");
  };

  const handleBackFromCreate = () => {
    setIsCreatingClass(false);
    fetchClasses(pagination.current, pagination.pageSize); // Refresh current page, not just page 1
  };

  return (
    <div>
      {!isCreatingClass && (
        <Card>
          <Row
            justify="space-between"
            align="middle"
            style={{ marginBottom: 20 }}
          >
            <Col>
              <Title level={4}>Classes</Title>
            </Col>
            <Col>
              <Button type="primary" onClick={handleCreateClass}>
                Create New Class
              </Button>
            </Col>
          </Row>
          <Table
            columns={columns}
            dataSource={classes}
            rowKey="class_name" // Or any other unique identifier
            loading={loading}
            scroll={{ x: "max-content", y: 550 }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
            }}
            onChange={handleTableChange}
          />
        </Card>
      )}

      {isCreatingClass && (
        <CreateClass
          onBack={handleBackFromCreate}
          fetchClasses={() =>
            fetchClasses(pagination.current, pagination.pageSize)
          }
          classData={classData} // Pass the class data to the CreateClass component
        />
      )}
    </div>
  );
};

export default ClassesList;
